// Host-side SQL transport for guest frames. Guests describe queries over the
// frame protocol (guest-sql); the transport runs them in the TRUSTED realm
// under the user's own auth — guest code never touches the network.
//
// The provider carries an LRU promise cache so identical statements from N
// guests collapse into one backend call.
import {
  type AppGraphqlOperation,
  type AppGraphqlSubscription,
  frameSqlCacheKey,
  type FrameSqlOptions,
  normalizeFrameSqlOptions,
  type PaginatedFrameQueryResult,
  type UdsRowLimitParameter,
} from '@doss/configbase-common/apps'
import { LRUCache } from 'lru-cache'
import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'

/** Caller options; the transport normalizes them before anything else. */
export type UdsSqlTransportOptions = FrameSqlOptions & {
  /**
   * W3C context for the backend fetch, not query semantics. A cache hit makes no fetch, so changing
   * trace context must not create another result-cache identity.
   */
  traceparent?: string
  /** Request-scoped cache bypass; excluded from identity and forwarded to the host adapter. */
  refresh?: boolean
}

export type UdsSqlTransportResult = {
  /** Engine-ordered rows; readonly — reordering here re-implements query semantics. */
  rows: readonly Record<string, unknown>[]
  /** UTF-8 byte length of the exact JSON-serialized rows array. */
  resultBytes: number
  /** Trace id of the backend fetch that produced this result, when the transport minted one. */
  traceId?: string
} & PaginatedFrameQueryResult

export type RunSql = (
  sql: string,
  options?: UdsSqlTransportOptions,
) => Promise<UdsSqlTransportResult>

export type UdsSqlTransportStatement = {
  sql: string
  options?: UdsSqlTransportOptions
}

export type UdsSqlTransportError = {
  message: string
  code?: string
  limitBytes?: number
  resultBytes?: number
  parameter?: UdsRowLimitParameter
  requestedRows?: number
  maxRows?: number
}

export type UdsSqlTransportOutcome =
  | { result: UdsSqlTransportResult }
  | { error: UdsSqlTransportError }

export type RunSqlBatch = (
  statements: readonly UdsSqlTransportStatement[],
) => Promise<readonly UdsSqlTransportOutcome[]>

export type GuestSqlTransportPromise = Promise<UdsSqlTransportResult> & {
  source: 'cache' | 'backend'
}

export type RunGuestSql = (
  sql: string,
  options?: UdsSqlTransportOptions,
) => GuestSqlTransportPromise

export type RunGuestSqlBatch = (
  statements: readonly UdsSqlTransportStatement[],
) => readonly GuestSqlTransportPromise[]

/** One visible column in the UDS schema exposed to embed tooling. */
export type UdsSchemaColumn = {
  /** UDS-facing name: the column slug or a __doss_* infrastructure name. */
  slug: string
  /** PostgreSQL type name as reported by the UDS catalog (e.g. text, numeric). */
  dataType: string
  isNullable: boolean
}

/** One table visible to the current user through the UDS SQL surface. */
export type UdsSchemaTable = {
  /** UDS-facing relation name: the table slug. */
  slug: string
  /** Infrastructure columns first, then application columns in table order. */
  columns: readonly UdsSchemaColumn[]
}

/**
 * Loads the UDS-queryable schema for the current user: exactly the tables and
 * columns `runSql` statements may reference, with UDS-reported PostgreSQL
 * types. Backed by the RLS-enforced `tables` query, not a separate catalog
 * endpoint, so it can never disagree with the caller's table/column grants.
 */
export type GetUdsSchema = () => Promise<readonly UdsSchemaTable[]>

/** Fail fast on surfaces without a data transport. */
export const runSqlUnavailable: RunSql = () =>
  Promise.reject(new Error('Guest data queries are not available yet'))

/** One endpoint call as the viewer, uncached here: the App frame's QueryClient caches reads. */
export type RunEndpoint = (call: {
  logicalId: string
  input: Record<string, unknown>
}) => Promise<unknown>

/** A failed endpoint call, in the endpoints service's vocabulary. */
export class EndpointCallError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'EndpointCallError'
  }
}

export const runEndpointUnavailable: RunEndpoint = () =>
  Promise.reject(new EndpointCallError('Endpoint calls are not available in this surface'))

/** Runs one of the App row documents (`APP_GRAPHQL_DOCUMENTS`) as the viewer, uncached here. */
export type RunGraphql = (
  operation: AppGraphqlOperation,
  variables: Record<string, unknown>,
) => Promise<unknown>

export const runGraphqlUnavailable: RunGraphql = () =>
  Promise.reject(new Error('App row reads are not available in this surface'))

/** Follows one of the App subscriptions (`APP_GRAPHQL_SUBSCRIPTIONS`) as the viewer; returns the stop. */
export type SubscribeGraphql = (
  operation: AppGraphqlSubscription,
  variables: Record<string, unknown>,
  observer: { readonly next: (data: unknown) => void; readonly error: (error: unknown) => void },
) => () => void

/** Surfaces without live App rows leave a subscription open with nothing to deliver. */
export const subscribeGraphqlUnavailable: SubscribeGraphql = () => () => undefined

/** Placeholder for surfaces that render guests without schema tooling. */
export const getUdsSchemaUnavailable: GetUdsSchema = () =>
  Promise.reject(new Error('The UDS schema is not available in this surface'))

// Per-provider memory ceiling; eviction only causes a refetch. Match the frame cache so one side
// cannot retain substantially more query identities than the other.
const MAX_QUERY_CACHE_ENTRIES = 128

type RuntimeContextValue = {
  runSql: RunGuestSql
  runSqlBatch: RunGuestSqlBatch
  getUdsSchema: GetUdsSchema
  runEndpoint: RunEndpoint
  runGraphql: RunGraphql
  subscribeGraphql: SubscribeGraphql
}

const GuestFrameRuntimeContext = createContext<RuntimeContextValue | null>(null)

type GuestFrameRuntimeProviderProps = {
  runSql: RunSql
  runSqlBatch?: RunSqlBatch
  getUdsSchema?: GetUdsSchema
  runEndpoint?: RunEndpoint
  runGraphql?: RunGraphql
  subscribeGraphql?: SubscribeGraphql
  children: ReactNode
}

export const GuestFrameRuntimeProvider = ({
  runSql,
  runSqlBatch,
  getUdsSchema = getUdsSchemaUnavailable,
  runEndpoint = runEndpointUnavailable,
  runGraphql = runGraphqlUnavailable,
  subscribeGraphql = subscribeGraphqlUnavailable,
  children,
}: GuestFrameRuntimeProviderProps): JSX.Element => {
  const value = useMemo<RuntimeContextValue>(() => {
    // Promise cache shared per provider (dedupes across guests). `get` refreshes recency, so
    // eviction is least-recently-used. Failed requests retry on the next call rather than caching
    // an error.
    const cache = new LRUCache<string, Promise<UdsSqlTransportResult>>({
      max: MAX_QUERY_CACHE_ENTRIES,
    })
    const cachedRunSql: RunGuestSql = (sql, options) => {
      const normalizedOptions = normalizeFrameSqlOptions(options)
      const key = frameSqlCacheKey(sql, normalizedOptions)
      const cached = options?.refresh === true ? undefined : cache.get(key)
      if (cached !== undefined) {
        return Object.assign(
          cached.then((result) => result),
          { source: 'cache' as const },
        )
      }
      const pending = runSql(sql, {
        ...normalizedOptions,
        ...(options?.refresh === true ? { refresh: true } : {}),
        ...(options?.traceparent === undefined ? {} : { traceparent: options.traceparent }),
      })
      cache.set(key, pending)
      pending.catch(() => {
        // Avoid refreshing a newer entry or reviving this failed one.
        if (cache.peek(key) === pending) {
          cache.delete(key)
        }
      })
      return Object.assign(
        pending.then((result) => result),
        { source: 'backend' as const },
      )
    }
    // Reuse the runSql LRU: fully cached batches skip the backend and misses seed the cache.
    const cachedRunSqlBatch: RunGuestSqlBatch = (statements) => {
      type BatchEntry =
        | { kind: 'hit'; cached: Promise<UdsSqlTransportResult> }
        | { kind: 'miss'; key: string; missIndex: number }
      const missStatements: UdsSqlTransportStatement[] = []
      const missIndexByKey = new Map<string, number>()
      const entries = statements.map(({ sql, options }): BatchEntry => {
        const normalizedOptions = normalizeFrameSqlOptions(options)
        const key = frameSqlCacheKey(sql, normalizedOptions)
        const cached = options?.refresh === true ? undefined : cache.get(key)
        if (cached !== undefined) {
          return { kind: 'hit', cached }
        }
        const existingIndex = missIndexByKey.get(key)
        if (existingIndex !== undefined) {
          return { kind: 'miss', key, missIndex: existingIndex }
        }
        const missIndex = missStatements.length
        missIndexByKey.set(key, missIndex)
        missStatements.push({
          sql,
          options: {
            ...normalizedOptions,
            ...(options?.refresh === true ? { refresh: true } : {}),
            ...(options?.traceparent === undefined ? {} : { traceparent: options.traceparent }),
          },
        })
        return { kind: 'miss', key, missIndex }
      })
      let pendingOutcomes: Promise<readonly UdsSqlTransportOutcome[]> | undefined
      const getPendingOutcomes = (): Promise<readonly UdsSqlTransportOutcome[]> =>
        (pendingOutcomes ??=
          runSqlBatch?.(missStatements) ??
          Promise.all(
            missStatements.map(async ({ sql, options }): Promise<UdsSqlTransportOutcome> => {
              try {
                return { result: await runSql(sql, options) }
              } catch (error) {
                return {
                  error: { message: error instanceof Error ? error.message : String(error) },
                }
              }
            }),
          ))
      return entries.map((entry) => {
        if (entry.kind === 'hit') {
          return Object.assign(
            entry.cached.then((result) => result),
            { source: 'cache' as const },
          )
        }
        const statementPending = getPendingOutcomes().then((outcomes) => {
          const outcome = outcomes.at(entry.missIndex)
          if (outcome === undefined) {
            throw new Error('UDS SQL batch returned fewer outcomes than statements')
          }
          if ('error' in outcome) {
            const { message, ...extensions } = outcome.error
            throw Object.assign(new Error(message), { extensions })
          }
          return outcome.result
        })
        cache.set(entry.key, statementPending)
        statementPending.catch(() => {
          // peek (not get): mirror cachedRunSql's eviction — drop only our own failed entry.
          if (cache.peek(entry.key) === statementPending) {
            cache.delete(entry.key)
          }
        })
        return Object.assign(
          statementPending.then((result) => result),
          { source: 'backend' as const },
        )
      })
    }

    // Single-flight schema load shared by every consumer under this provider;
    // failures clear so the next call retries instead of caching an error.
    const schemaSlot: { pending: Promise<readonly UdsSchemaTable[]> | null } = { pending: null }
    const cachedGetUdsSchema: GetUdsSchema = () => {
      if (schemaSlot.pending !== null) {
        return schemaSlot.pending
      }
      const pending = getUdsSchema()
      schemaSlot.pending = pending
      pending.catch(() => {
        if (schemaSlot.pending === pending) {
          schemaSlot.pending = null
        }
      })
      return pending
    }
    return {
      runSql: cachedRunSql,
      runSqlBatch: cachedRunSqlBatch,
      getUdsSchema: cachedGetUdsSchema,
      runEndpoint,
      runGraphql,
      subscribeGraphql,
    }
  }, [runSql, runSqlBatch, getUdsSchema, runEndpoint, runGraphql, subscribeGraphql])

  return (
    <GuestFrameRuntimeContext.Provider value={value}>{children}</GuestFrameRuntimeContext.Provider>
  )
}

/** The provider-scoped, cache-backed raw SQL transport used by guest frames. */
export const useGuestSqlTransport = (): RunGuestSql => {
  const context = useContext(GuestFrameRuntimeContext)
  if (context === null) {
    throw new Error('useGuestSqlTransport requires GuestFrameRuntimeProvider')
  }
  return context.runSql
}

/** Render-scoped batch transport: cache misses share one backend snapshot session per call. */
export const useGuestSqlBatchTransport = (): RunGuestSqlBatch => {
  const context = useContext(GuestFrameRuntimeContext)
  if (context === null) {
    throw new Error('useGuestSqlBatchTransport requires GuestFrameRuntimeProvider')
  }
  return context.runSqlBatch
}

/** The provider-scoped, single-flight UDS schema loader for embed tooling. */
export const useGuestUdsSchema = (): GetUdsSchema => {
  const context = useContext(GuestFrameRuntimeContext)
  if (context === null) {
    throw new Error('useGuestUdsSchema requires GuestFrameRuntimeProvider')
  }
  return context.getUdsSchema
}

/** The provider's endpoint transport: each call goes out as the viewer. */
export const useGuestEndpointTransport = (): RunEndpoint => {
  const context = useContext(GuestFrameRuntimeContext)
  if (context === null) {
    throw new Error('useGuestEndpointTransport requires GuestFrameRuntimeProvider')
  }
  return context.runEndpoint
}

/** The provider's App row reads: each runs as the viewer; the frame's Apollo cache owns caching. */
export const useGuestGraphqlTransport = (): RunGraphql => {
  const context = useContext(GuestFrameRuntimeContext)
  if (context === null) {
    throw new Error('useGuestGraphqlTransport requires GuestFrameRuntimeProvider')
  }
  return context.runGraphql
}

/** The provider's App subscriptions: each runs as the viewer. */
export const useGuestSubscriptionTransport = (): SubscribeGraphql => {
  const context = useContext(GuestFrameRuntimeContext)
  if (context === null) {
    throw new Error('useGuestSubscriptionTransport requires GuestFrameRuntimeProvider')
  }
  return context.subscribeGraphql
}
